package com.downdogapp.client.controllers

import com.downdogapp.*
import com.downdogapp.client.MANIFEST
import com.downdogapp.client.Strings
import com.downdogapp.client.ViewController
import com.downdogapp.client.api.*
import com.downdogapp.client.api.FeedbackType.AFFORDABILITY
import com.downdogapp.client.api.ProductPeriod.MONTHLY
import com.downdogapp.client.api.ProductPeriod.YEARLY
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.Platform.ANDROID
import com.downdogapp.client.singleton.Platform.IOS
import com.downdogapp.client.singleton.Platform.WEB
import com.downdogapp.client.views.PurchaseView

class PurchaseViewController(private val alertMessage: String? = null, private val onPop: () -> Unit = {}) : ViewController() {

  private var paymentPending = false

  val config = MANIFEST.membershipConfig
  val affordabilityText = config.affordabilityMessage?.title ?: config.affordabilityLink?.label
  private val products = config.products.toSet()

  val alternateCurrencyCode = products.firstOrNull { it.currencyCode != config.defaultCurrencyCode }?.currencyCode

  val yearlySavingsPercentageString = config.yearlySavingsPercentageString

  val leftToggleProductPeriod = YEARLY

  val rightToggleProductPeriod = MONTHLY

  var prices: Map<Pair<ProductPeriod, String?>, String>? = null
  var pricesPerMonth: Map<Pair<ProductPeriod, String?>, String?>? = null

  private var wasBackgrounded = false

  override val view = PurchaseView(this)

  init {
    if (App.platform != WEB) {
      check(config.defaultCurrencyCode == null)
      check(products.all { it.currencyCode == null })
    } else {
      check(config.defaultCurrencyCode != null)
      check(products.all { it.currencyCode != null && it.price != null })
      check(products.mapToSet { it.currencyCode }.size <= 2)
    }
    checkForPrices()
  }

  override fun onViewBecameVisible() {
    if (App.platform == WEB) {
      // We don't do this on mobile, but because they might just close the tab, it's safer to
      // log a declined purchase right away
      Network.post(RecordPurchaseRequest(trial = false))
    }

    alertMessage?.let {
      App.alert(message = alertMessage)
    }
  }

  private fun checkForPrices() {
    if (products.all { it.productId in App.pricesMap }) {
      prices =
        products.associate { product ->
          (product.productPeriod to product.currencyCode) to
            (App.pricesMap[product.productId]?.yearlyPriceString ?: "")
        }
      pricesPerMonth =
        products.associate { product ->
          (product.productPeriod to product.currencyCode) to
            if (App.platform in setOf(ANDROID, IOS) && product.productPeriod == YEARLY)
              App.pricesMap[product.productId]?.let {
                "${it.monthlyPriceString} / ${Strings.MONTH}"
              }
            else
              null
        }
      view.refreshView()
    } else {
      App.runAfter(seconds(0.1)) {
        checkForPrices()
      }
    }
  }

  override fun onBackgrounded() {
    wasBackgrounded = true
  }

  override fun onForegrounded() {
    if (wasBackgrounded) {
      view.showSpinner()
      fetchManifestAndUnwind()
    }
  }

  fun payWithCreditCardClicked() {
    if (!paymentPending) {
      view.showSpinner()
      AppHelper.payWithCreditCard(this)
    }
  }

  fun onPurchaseFailed(errorMessage: String?) {
    if (errorMessage == null) {
      App.alertNoInternet()
    } else {
      App.alert(title = Strings.ERROR_OCCURRED_MESSAGE, message = errorMessage)
    }
    paymentPending = false
    view.hideSpinner()
  }

  override fun onBackClicked() {
    if (!paymentPending) {
      logEvent("declined_membership")
      paymentPending = true
      view.showSpinner()
      Network.post(RecordPurchaseRequest(trial = false)) {
        if (App.platform == WEB) {
          view.hideSpinner()
          App.goHomeOrOpenApp()
        } else {
          fetchManifestAndUnwind()
        }
      }
    }
  }

  fun fetchManifestAndUnwind() { // android and ios
    App.fetchManifest(loadStoredOnFailure = false) {
      App.popViewController()
      onPop()
    }
  }

  fun promoClicked() {
    App.openUrl(config.webPromoUrl!!)
  }

  fun affordabilityClicked() {
    if (config.affordabilityMessage != null) {
      App.pushViewController(
        MessageViewController(message = config.affordabilityMessage, feedbackType = AFFORDABILITY)
      )
    } else {
      App.openFirstValidUrl(config.affordabilityLink!!.urls)
    }
  }

  fun purchaseClicked(productId: String) {
    fun onConfirm() {
      view.showSpinner()
      AppHelper.payInApp(productId, this)
    }

    if (config.purchaseConfirmationText == null) {
      onConfirm()
    } else {
      App.alert(
        message = config.purchaseConfirmationText,
        negativeButton = AlertAction(Strings.CANCEL) {},
        positiveButton = AlertAction(Strings.OK) {
          onConfirm()
        }
      )
    }
  }

  fun getProduct(productPeriod: ProductPeriod,
                 currencyCode: String? = null) =
    products
      .single {
        it.productPeriod == productPeriod
        && it.currencyCode == currencyCode
      }

  fun termsClicked() {
    App.openUrl("https://www.downdogapp.com/terms")
  }

  fun privacyClicked() {
    App.openUrl("https://www.downdogapp.com/privacy")
  }

  fun hideSpinner() {
    view.hideSpinner()
  }

  fun showSpinner() {
    view.showSpinner()
  }

  fun getImage(appType: AppType) =
    when (appType) {
      AppType.ORIGINAL -> Images.PURCHASE_TILE_ACTIVE_ORIGINAL
      AppType.INTRO, AppType.SEVEN, AppType.PRENATAL -> null
      AppType.HIIT -> Images.PURCHASE_TILE_ACTIVE_HIIT
      AppType.BARRE -> Images.PURCHASE_TILE_ACTIVE_BARRE
      AppType.MEDITATION -> Images.PURCHASE_TILE_ACTIVE_MEDITATION
      AppType.RUNNING -> Images.PURCHASE_TILE_ACTIVE_RUNNING
      AppType.BREATHING -> Images.PURCHASE_TILE_ACTIVE_BREATHING
      AppType.PILATES -> Images.PURCHASE_TILE_ACTIVE_PILATES
    }

  companion object {

    fun onPurchaseSuccessful() {
      Logger.logEvent("purchase_success")
      App.alert(message = Strings.PAYMENT_CONFIRMED_MESSAGE) {
        App.goHomeOrOpenApp()
      }
    }
  }
}

data class YearlyMonthlyPricePair(val yearlyPriceString: String, val monthlyPriceString: String?)
